import { useEffect } from "react";
// React Query
import { useQuery, useQueryClient } from "@tanstack/react-query";
// MUI
import { Divider } from "@mui/material";

// Context
import { useCRMContext } from "@/DataPages/context/crm/CRMContext";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// API
import { getSocialMediaReview } from "@/DataPages/api/socialMediaReviews/queries";
import { onCreateReviewResponseSummaryByLocation, onCreateSocialMediaReviews } from "@DataPages/api/socialMediaReviews/subscriptions";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";

// Helpers
import { getAssociateOptions, getTransformedReputationData } from "./helpers";
import { and, or } from "@/Common/helpers/sonar";

// Components
import SocialMediaReviewStoreSection from "./store/SocialMediaReviewStoreSection";
import SocialMediaReviewAssociatesSection from "./positions/SocialMediaReviewAssociatesSection";
import DataSyncInfo from "./Widgets/DataSyncInfo";

// Constants
import { CRM_KEYS } from "../constants";

// Types
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IReviewData } from "@/DataPages/types/socialMediaReviews/model";

type TSocialMediaReviewsProps = {
	isActiveTab: boolean;
};

export default function SocialMediaReviews(props: Readonly<TSocialMediaReviewsProps>) {
	const { isActiveTab } = props;
	const location = useLocation();
	const { timezone, locationId } = location ?? {};

	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	const { selection } = useCRMContext();
	const { cycleId: commissionCycleId = "", payPeriodId = "" } = selection ?? {};
	const { commissionCycle = null, payPeriod } = useCommissionCyclesData({ commissionCycleId, payPeriodId });

	const selectedItem = {
		commissionCycleId: commissionCycle?._id as string,
		payPeriodId: payPeriod?._id as string,
		title: `${commissionCycle?.title} ${payPeriod?.title} Reviews`,
		startDateCommissionCycle: (commissionCycle?.startDate ?? "") as string,
		payPeriodEndDate: (payPeriod?.endDate ?? "") as string,
	};

	const isUpdatable = Boolean(commissionCycle?.status === "CLOSING" || commissionCycle?.status === "ACTIVE");

	/********************************************* QUERIES ************************************************/
	const { isLoading, data: reputationReviewsData } = useQuery({
		queryKey: [CRM_KEYS.QUERY.REPUTATION_REVIEWS_DATA_ROW, locationId, commissionCycleId, payPeriodId],
		queryFn: async () => {
			const response = await getSocialMediaReview({
				timezone: timezone as string,
				locationId: locationId as string,
				payPeriodEndDate: (payPeriod?.endDate ?? "") as string,
				commissionCycleId: commissionCycleId as string,
			});
			return getTransformedReputationData(response);
		},
		enabled: and(isActiveTab, Boolean(locationId), Boolean(commissionCycleId), Boolean(payPeriod?.endDate), Boolean(timezone)),
		staleTime: Infinity,
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const socialMediaReviewsSubscription = getSubscription([CRM_KEYS.SUBSCRIPTION.SOCIAL_MEDIA_REVIEW, commissionCycleId]);
	socialMediaReviewsSubscription?.subscriptions?.onCreateSocialMediaReviews?.setCallback(
		(result: GraphqlSubscriptionResponse<"onCreateSocialMediaReviews", IReviewData>) => {
			const reviews = result.data?.onCreateSocialMediaReviews;

			if (!reviews) {
				return reviews;
			}

			const transformed = getTransformedReputationData(reviews);
			queryClient.setQueryData(
				[CRM_KEYS.QUERY.REPUTATION_REVIEWS_DATA_ROW, locationId, commissionCycleId, payPeriod?.endDate ?? ""],
				() => transformed
			);
		}
	);

	const createReviewSubscriptions = getSubscription(["createReviewResponseSummaryByLocation", locationId, commissionCycleId]);
	createReviewSubscriptions?.subscriptions?.onCreateReviewResponseSummaryByLocation?.setCallback(() => {
		queryClient.invalidateQueries({
			queryKey: [CRM_KEYS.QUERY.REPUTATION_REVIEWS_DATA_ROW, locationId, commissionCycleId],
			exact: false,
			type: "all",
			refetchType: "all",
		});

		queryClient.invalidateQueries({
			queryKey: ["reputationReviewsDetailsData", locationId, commissionCycleId],
			exact: false,
			type: "all",
			refetchType: "all",
		});
	});

	useEffect(() => {
		// Subscribing to the mutations that update the review summary and the one that updates the number of reviews needing response
		if (locationId && commissionCycleId) {
			addSubscription(
				[CRM_KEYS.SUBSCRIPTION.SOCIAL_MEDIA_REVIEW, commissionCycleId],
				[
					{
						context: "onCreateSocialMediaReviews",
						query: onCreateSocialMediaReviews,

						variables: {
							commissionCycleId,
						},
					},
				]
			);
			addSubscription(
				["createReviewResponseSummaryByLocation", locationId, commissionCycleId],
				[
					{
						context: "onCreateReviewResponseSummaryByLocation",
						query: onCreateReviewResponseSummaryByLocation,
						variables: { locationId, commissionCycleId },
					},
				]
			);
		}
	}, [locationId, commissionCycleId]);

	return (
		<>
			<SocialMediaReviewStoreSection
				// Data
				locationId={locationId}
				isLoading={or(isLoading, !Boolean(payPeriodId))}
				selectedItem={selectedItem}
				isDataPresent={Boolean(reputationReviewsData?.dataStoreReviews?.length)}
				// Table Data
				rows={reputationReviewsData?.dataStoreReviews || []}
				columns={reputationReviewsData?.storeReviewsColumns || []}
			/>

			{(reputationReviewsData?.dataStoreReviews?.length || isLoading) && <Divider className="w-full !mt-5" />}

			<SocialMediaReviewAssociatesSection
				options={getAssociateOptions(reputationReviewsData?.usersReviews)}
				locationId={locationId}
				selectedItem={selectedItem}
				positionsContent={reputationReviewsData?.usersReviews || []}
				isLoading={or(isLoading, !Boolean(payPeriodId))}
				isDataPresent={Boolean(reputationReviewsData?.usersReviews?.length)}
				isUpdatable={isUpdatable}
				onDemand={reputationReviewsData?.onDemand}
			/>

			<DataSyncInfo
				className={Boolean(reputationReviewsData?.usersReviews?.length) || isLoading ? "" : "!mt-8"}
				payPeriodEndDate={selectedItem.payPeriodEndDate}
				isLoading={or(isLoading, !Boolean(payPeriodId))}
				timezone={timezone}
				data={reputationReviewsData}
				isUpdatable={isUpdatable}
				commissionCycleId={commissionCycleId}
				locationId={locationId}
			/>
		</>
	);
}
